<template>
  <div class="map-application">
    <div class="main-container">
      <div class="map-frame">
        <div class="map-search" @click.stop @mousedown.stop @touchstart.stop>
          <v-text-field
            v-model="busqueda"
            dense
            outlined
            clearable
            hide-details
            prepend-inner-icon="mdi-magnify"
            placeholder="Buscar activo"
            class="map-search-input"
          />
        </div>
        <div ref="mapaPlantilla" class="plantilla-leaflet-map"></div>

        <div class="map-legend">
          <div class="legend-title">Plantilla</div>

          <div class="legend-row">
            <span class="legend-dot activo"></span>
            <span>Activado</span>
          </div>

          <div class="legend-row">
            <span class="legend-dot no-activo"></span>
            <span>No activado</span>
          </div>
        </div>

        <div
          v-if="locations.length === 0"
          class="empty-map-message"
        >
          No hay activos con coordenadas para mostrar.
        </div>
      </div>
    </div>

    <!-- Modal histórico de activo -->
    <v-dialog v-model="historyDialog" max-width="560px" :z-index="3000">
      <v-card rounded="lg">
        <v-card-title class="headline grey lighten-4">
          <span class="modal-history-title">
            Histórico
          </span>
        </v-card-title>

        <v-card-text class="pa-4">
          <v-progress-linear
            v-if="loadingHistory"
            indeterminate
            color="primary"
            class="mb-4"
          />

          <div
            v-if="!loadingHistory && assetHistory.length === 0"
            class="text-center py-4 grey--text"
          >
            Este activo todavía no tiene histórico de puntos.
          </div>

          <div v-if="!loadingHistory && assetHistory.length > 0">
            <div class="text-caption grey--text mb-3">
              Últimas 5 puntuaciones registradas para este activo.
            </div>

           <v-simple-table dense>
            <thead>
              <tr>
                <th>Fecha</th>
                <th class="text-right">Ganancia</th>
                <th class="text-right">Batería</th>
                <th class="text-right">Puntos</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="(h, index) in assetHistory" :key="index">
                <td>
                  {{ formatDate(h.fecha) }}
                </td>

                <td class="text-right">
                  {{ money(h.ganancia_activo) }}
                </td>

                <td class="text-right">
                  <span
                    v-if="Number(h.puntos_bateria || 0) !== 0"
                    class="battery-history-points"
                  >
                    {{ formatPoints(h.puntos_bateria) }} pts
                  </span>

                  <span v-else class="grey--text">
                    —
                  </span>
                </td>

                <td class="text-right">
                  <span
                    class="asset-history-points"
                    :class="
                      Number(h.puntos_activo || 0) >= 0
                        ? 'green--text'
                        : 'red--text'
                    "
                  >
                    {{ formatPoints(h.puntos_activo) }}
                  </span>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
          </div>
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" text @click="historyDialog = false">
            Cerrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Fin modal histórico de activo -->
  </div>
</template>

<script>
import { useGameStore } from "@/stores/game";
import Swal from "sweetalert2";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

export default {
  name: "TacticalMap",

  props: {
    unidades: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      store: useGameStore(),

      leafletMap: null,
      layerActivos: null,
      resizeTimer: null,

      locations: [],

      historyDialog: false,
      selectedAssetHistory: null,
      assetHistory: [],
      loadingHistory: false,
      historyLoadingId: null,
      busqueda: "",
    };
  },

  mounted() {
    this.$nextTick(() => {
      this.buildLocations();
      this.initMap();
      this.drawActivos();
      this.scheduleResize();
    });
  },

  beforeUnmount() {
    this.destroyMap();
  },

  beforeDestroy() {
    this.destroyMap();
  },

  watch: {
    unidades: {
      immediate: true,
      deep: true,
      handler() {
        this.buildLocations();
        this.$nextTick(() => {
          this.drawActivos();
        });
      },
    },
    busqueda() {
    this.drawActivos();
  },
  },

  methods: {
    normalizarTexto(v) {
  return (v || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
},

pasaBusqueda(point) {
  const q = this.normalizarTexto(this.busqueda);

  if (!q) return true;

  const texto = [
    point.name,
    point.tipo,
    point.municipio,
    point.provincia,
    point.capacity,
  ]
    .map(this.normalizarTexto)
    .join(" ");

  return texto.includes(q);
},
    initMap() {
      if (this.leafletMap || !this.$refs.mapaPlantilla) return;

      this.leafletMap = L.map(this.$refs.mapaPlantilla, {
        zoomControl: true,
        zoomAnimation: false,
        fadeAnimation: false,
      }).setView([40.3, -3.7], 6);

      const cartoApiKey = process.env.VUE_APP_CARTO_API_KEY;

      L.tileLayer(
        `https://{s}.basemaps.cartocdn.com/rastertiles/light_all/{z}/{x}/{y}.png?key=${cartoApiKey}`,
        {
          subdomains: "abcd",
          maxZoom: 20,
          attribution: "© OpenStreetMap, © CARTO",
        }
      ).addTo(this.leafletMap);
    },

    destroyMap() {
      clearTimeout(this.resizeTimer);

      if (this.leafletMap) {
        this.leafletMap.remove();
        this.leafletMap = null;
      }

      this.layerActivos = null;
    },

    scheduleResize() {
      clearTimeout(this.resizeTimer);

      this.resizeTimer = setTimeout(() => {
        requestAnimationFrame(() => {
          try {
            if (this.leafletMap) {
              this.leafletMap.invalidateSize();
            }
          } catch (e) {
            console.error("[MyMap] resize error:", e);
          }
        });
      }, 120);
    },

    buildLocations() {
      this.locations = (this.unidades || [])
        .map((loc) => {
          const lng = Number(
            loc.x ??
            loc.lng ??
            loc.lon ??
            loc.longitude
          );

          const lat = Number(
            loc.y ??
            loc.lat ??
            loc.latitude
          );

          if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
            console.warn("Activo sin coordenadas válidas:", loc);
            return null;
          }

          const batteryCount = Array.isArray(loc.baterias)
            ? loc.baterias.length
            : 0;

          const hasBattery = batteryCount > 0;
          const operativo = Number(loc.operativo || 0) === 1;

          return {
            id: loc.id,
            asset_id: loc.asset_id || loc.activo_id || loc.id,
            name: loc.name || loc.nombre || "Activo",
            tipo: loc.type || loc.tecnologia || "—",
            capacity: loc.capacity || loc.mw || null,
            provincia: loc.provincia || "",
            municipio: loc.municipio || "",
            lat,
            lng,
            operativo,
            batteryCount,
            hasBattery,
            original: loc,
          };
        })
        .filter(Boolean);
    },

    drawActivos() {
      if (!this.leafletMap) return;

      if (this.layerActivos) {
        this.layerActivos.removeFrom(this.leafletMap);
        this.layerActivos = null;
      }

      const group = L.featureGroup();
      const usados = {};
      const locationsFiltradas = this.locations.filter(this.pasaBusqueda);

      locationsFiltradas.forEach((point) => {
        const key = `${point.lng}|${point.lat}`;
        const index = usados[key] || 0;
        usados[key] = index + 1;

        let finalLat = point.lat;
        let finalLng = point.lng;

        if (index > 0) {
          const step = 0.015;
          const angle = (index - 1) * (Math.PI / 4);
          finalLat = point.lat + Math.sin(angle) * step;
          finalLng = point.lng + Math.cos(angle) * step;
        }

        const color = point.operativo ? "#2e7d32" : "#f39c12";
        const estado = point.operativo ? "Activado" : "No activado";

        const marker = L.circleMarker([finalLat, finalLng], {
          radius: 11,
          color: "#ffffff",
          weight: 3,
          fillColor: color,
          fillOpacity: 1,
          opacity: 1,
          className: point.operativo
            ? "plantilla-marker-activo"
            : "plantilla-marker-no-activo",
        });

        const tooltip = `
          <div style="min-width: 170px;">
            <b>${point.name}</b><br/>
            <b>Tecnología:</b> ${point.tipo}<br/>
            ${
              point.capacity != null
                ? `<b>MW:</b> ${point.capacity}<br/>`
                : ""
            }
            ${
              point.municipio
                ? `<b>Municipio:</b> ${point.municipio}<br/>`
                : ""
            }
            ${
              point.provincia
                ? `<b>Provincia:</b> ${point.provincia}<br/>`
                : ""
            }
            <b>Estado:</b>
            <span style="font-weight:700;color:${color};">
              ${estado}
            </span>
            ${
              point.hasBattery
                ? `<br/><b>Baterías:</b> ${point.batteryCount}`
                : ""
            }
          </div>
        `;

        marker.bindTooltip(tooltip, {
          direction: "top",
          offset: [0, -8],
          opacity: 0.95,
          sticky: true,
        });

        marker.on("click", () => {
          this.openAssetHistory(point);
        });

        marker.addTo(group);

        if (point.hasBattery) {
          const batteryIcon = L.divIcon({
            className: "battery-leaflet-icon",
            html: `
              <div class="battery-leaflet-badge">
                <span class="material-symbols-outlined battery-leaflet-symbol">
                  battery_4_bar
                </span>
                ${
                  point.batteryCount > 1
                    ? `<span class="battery-leaflet-count">${point.batteryCount}</span>`
                    : ""
                }
              </div>
            `,
            iconSize: [22, 22],
            iconAnchor: [-2, 18],
          });

          L.marker([finalLat, finalLng], {
            icon: batteryIcon,
            interactive: false,
            keyboard: false,
          }).addTo(group);
        }
      });

      this.layerActivos = group.addTo(this.leafletMap);

      const isMobile = window.innerWidth <= 600;

      this.leafletMap.setView(
        [40.25, -3.7],
        isMobile ? 5.6 : 6.2,
        { animate: false }
      );

      this.scheduleResize();
    },

    money(v) {
      return `${Number(v || 0).toLocaleString("es-ES")} €`;
    },

    formatDate(v) {
      if (!v) return "-";

      const d = new Date(v);

      if (Number.isNaN(d.getTime())) return "-";

      return d.toLocaleDateString("es-ES", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    },

    formatPoints(v) {
      const n = Number(v || 0);

      if (n > 0) return `+${n}`;

      return `${n}`;
    },

    async openAssetHistory(asset) {
      const assetId = asset.asset_id || asset.activo_id || asset.id;

      if (!assetId) {
        Swal.fire({
          icon: "error",
          title: "No se pudo cargar el histórico",
          text: "Este activo no tiene identificador.",
        });
        return;
      }

      this.selectedAssetHistory = asset;
      this.historyDialog = true;
      this.loadingHistory = true;
      this.historyLoadingId = assetId;
      this.assetHistory = [];

      try {
        this.assetHistory = await this.store.fetchMyAssetHistory(assetId);
      } catch (e) {
        Swal.fire({
          icon: "error",
          title: "No se pudo cargar el histórico",
          text: e.message || "Ha ocurrido un error inesperado.",
        });
      } finally {
        this.loadingHistory = false;
        this.historyLoadingId = null;
      }
    },
  },
};
</script>

<style scoped>
.map-application {
  display: flex;
  justify-content: center;
  width: 100%;
  padding: 12px 0;
  font-family: "Segoe UI", sans-serif;
  box-sizing: border-box;
}

.main-container {
  width: 100%;
  display: flex;
  justify-content: center;
}

.map-frame {
  width: 100%;
  min-height: 430px;
  height: 520px;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0,0,0,0.18);
  border: 2px solid #dbe9e7;
  position: relative;
  z-index: 0;
  isolation: isolate;
  background: #eef2f5;
}

.plantilla-leaflet-map {
  width: 100%;
  height: 100%;
  min-height: 430px;
}

.map-legend {
  position: absolute;
  right: 12px;
  bottom: 12px;
  z-index: 1001;
  background: white;
  border-radius: 12px;
  padding: 10px 12px;
  box-shadow: 0 6px 20px rgba(0,0,0,0.16);
  font-size: 12px;
}

.legend-title {
  font-weight: 700;
  margin-bottom: 6px;
  color: #244946;
}

.legend-row {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-top: 4px;
}

.legend-dot {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 999px;
  border: 2px solid white;
  box-shadow: 0 1px 4px rgba(0,0,0,0.3);
}

.legend-dot.activo {
  background: #2e7d32;
}

.legend-dot.no-activo {
  background: #f39c12;
}

.empty-map-message {
  position: absolute;
  left: 50%;
  top: 50%;
  z-index: 1002;
  transform: translate(-50%, -50%);
  background: white;
  border-radius: 12px;
  padding: 12px 16px;
  box-shadow: 0 6px 20px rgba(0,0,0,0.14);
  color: #666;
  font-size: 13px;
}

:deep(.leaflet-container) {
  z-index: 0;
  font-family: "Segoe UI", sans-serif;
}

:deep(.plantilla-marker-activo) {
  filter:
    drop-shadow(0 0 5px rgba(255,255,255,1))
    drop-shadow(0 0 13px rgba(46,125,50,0.75));
}

:deep(.plantilla-marker-no-activo) {
  filter:
    drop-shadow(0 0 5px rgba(255,255,255,1))
    drop-shadow(0 0 13px rgba(243,156,18,0.85));
}

:deep(.battery-leaflet-icon) {
  background: transparent;
  border: none;
}

:deep(.battery-leaflet-badge) {
  position: relative;
  width: 21px;
  height: 21px;
  border-radius: 999px;
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  border: 2px solid #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 7px rgba(29, 78, 216, 0.4);
}

:deep(.battery-leaflet-symbol) {
  font-size: 13px !important;
  color: #ffffff;
  line-height: 1;
}

:deep(.battery-leaflet-count) {
  position: absolute;
  bottom: -7px;
  right: -7px;
  min-width: 14px;
  height: 14px;
  padding: 0 3px;
  border-radius: 999px;
  background: #0f172a;
  color: #ffffff;
  font-size: 9px;
  font-weight: 700;
  line-height: 14px;
  text-align: center;
  border: 1px solid #ffffff;
}

.modal-history-title {
  font-family: "Orbitron", sans-serif !important;
  color: #31635f;
  font-weight: 700;
}

.asset-history-points {
  font-family: "Orbitron", sans-serif !important;
  font-weight: 700;
}
.map-search {
  position: absolute;
  top: 12px;
  left: 60px;
  z-index: 1001;
  width: min(350px, calc(100% - 24px));
  background: rgba(255, 255, 255, 0.96);
  border-radius: 12px;
  box-shadow: 0 6px 20px rgba(0,0,0,0.14);
}

.map-search-input {
  font-size: 13px;
}

:deep(.map-search-input .v-input__slot) {
  margin-bottom: 0 !important;
  min-height: 38px !important;
}

.battery-history-points {
  margin-top: 2px;
  color: #1976d2;
  font-family: "Orbitron", sans-serif !important;
  font-size: 11px;
  font-weight: 700;
}

@media (max-width: 960px) {
  .map-frame {
    height: 460px;
    min-height: 400px;
  }

  .plantilla-leaflet-map {
    min-height: 400px;
  }
}

@media (max-width: 600px) {
  .map-application {
    padding: 6px 0;
  }

  .map-frame {
    height: 340px;
    min-height: 320px;
    border-radius: 12px;
  }

  .plantilla-leaflet-map {
    min-height: 320px;
  }

  .map-legend {
    right: 8px;
    bottom: 8px;
    padding: 8px 10px;
    font-size: 11px;
  }
  
}
@media (max-width: 600px) {
  .map-search {
    top: 8px;
    left: 8px;
    width: calc(100% - 16px);
  }
}
</style>